import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Stage, Layer, Image, Text, Transformer } from "react-konva";
import Konva from "konva";
import { useAppStore } from "../utils/state";
import type { ImageFile } from "../utils/state";
import { Button } from "@mantine/core";

interface ImageSpotProps {
  image: ImageFile;
}

export function Images({ image }: ImageSpotProps) {
  const { fontStyle, text, setImage } = useAppStore();
  const [stageSize, setStageSize] = React.useState({ width: 0, height: 0 });
  const [textPosition, setTextPosition] = React.useState({ x: 50, y: 50 });

  const containerRef = useRef<HTMLDivElement>(null);
  const originalImgRef = useRef<HTMLImageElement | null>(null);
  const processedImgRef = useRef<HTMLImageElement | null>(null);
  const stageRef = useRef<Konva.Stage>(null);

  const transformerRef = useRef<Konva.Transformer>(null);
  const textRef = useRef<Konva.Text>(null);
  const imageRef = useRef<Konva.Image>(null);

  const url = useMemo(() => URL.createObjectURL(image.file), [image.file]);
  const processedURL = useMemo(
    () => (image.processedFile ? URL.createObjectURL(image.processedFile) : ""),
    [image.processedFile]
  );
  const isProcessing = !image.processedFile;

  const updateStageSize = useCallback(() => {
    if (containerRef.current && originalImgRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      const imgWidth = originalImgRef.current.width;
      const imgHeight = originalImgRef.current.height;
      let scale = containerWidth / imgWidth;

      // TODO: adjust text position on resize

      const newHeight = imgHeight * scale;

      if (newHeight > containerHeight) {
        scale = containerHeight / imgHeight;
        setStageSize({
          width: imgWidth * scale,
          height: containerHeight,
        });
      } else {
        setStageSize({
          width: containerWidth,
          height: imgHeight * scale,
        });
      }
    }
  }, [containerRef, originalImgRef]);

  const handleExport = () => {
    if (stageRef.current) {
      const dataURL = stageRef.current.toDataURL();
      const link = document.createElement("a");
      link.download = `image-type-download.png`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      setTextPosition({
        x: e.target.x(),
        y: e.target.y(),
      });
    },
    [setTextPosition]
  );

  const memoizedStage = useMemo(() => {
    if (!stageSize.width || !stageSize.height) return null;
    return (
      <Stage ref={stageRef} width={stageSize.width} height={stageSize.height}>
        <Layer>
          {originalImgRef.current && (
            <Image
              ref={imageRef}
              image={originalImgRef.current}
              width={stageSize.width}
              height={stageSize.height}
              onClick={() => {
                if (transformerRef.current) {
                  transformerRef.current.nodes([]);
                }
              }}
            />
          )}
          <Text
            ref={textRef}
            text={text}
            x={textPosition.x}
            y={textPosition.y}
            fontSize={100}
            {...fontStyle}
            draggable
            onDragEnd={handleDragEnd}
            onClick={(e) => {
              if (transformerRef.current) {
                transformerRef.current.nodes([e.target]);
              }
            }}
          />
          {processedImgRef.current && (
            <Image
              image={processedImgRef.current}
              width={stageSize.width}
              height={stageSize.height}
              opacity={1}
              listening={false}
            />
          )}
          <Transformer
            ref={transformerRef}
            flipEnabled={false}
            enabledAnchors={[
              "top-left",
              "top-right",
              "bottom-left",
              "bottom-right",
            ]}
            boundBoxFunc={(oldBox, newBox) => {
              // limit resize
              if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                return oldBox;
              }
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    );
  }, [
    stageSize,
    text,
    textPosition,
    handleDragEnd,
    originalImgRef.current,
    processedImgRef.current,
    fontStyle,
  ]);

  useEffect(() => {
    if (textRef.current) {
      // Force recalculation by updating the text
      textRef.current.text(text);
      if(transformerRef.current)
      transformerRef.current.nodes([textRef.current]);
    }
  }, [fontStyle, text]);

  // select text element on load
  useEffect(() => {
    if (textRef.current && transformerRef.current) {
      transformerRef.current.nodes([textRef.current]);
    }
  }, [textRef.current, transformerRef.current]);

  // text width
  useEffect(() => {
    if (imageRef.current && textRef.current) {
      const x = imageRef.current.width() / 2;
      const y = imageRef.current.height() / 8;

      const textWidth = textRef.current.width();

      setTextPosition({ x: x - textWidth / 2, y });
    }
  }, [textRef.current, imageRef.current]);

  // Resize
  useEffect(() => {
    const originalImg = new window.Image();
    originalImg.src = url;
    originalImg.onload = () => {
      originalImgRef.current = originalImg;
      updateStageSize();
    };

    const processedImg = new window.Image();
    processedImg.src = processedURL;
    processedImg.onload = () => {
      processedImgRef.current = processedImg;
      updateStageSize();
    };

    const handleResize = () => {
      updateStageSize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      originalImgRef.current = null;
      processedImgRef.current = null;
    };
  }, [processedURL, updateStageSize]);

  return (
    <div className="overflow-hidden max-h-[calc(100vh-80px)] h-[calc(100vh-80px)] relative grid place-items-center bg-gray-100">
      {isProcessing ? (
        <>
          <div className="w-full  h-[calc(100vh-80px)]">
            <img
              className="max-w-full max-h-full object-contain opacity-50 transition-opacity duration-200 mx-auto"
              src={url}
              alt={`Processing image`}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black bg-opacity-50 px-4 py-2 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="animate-spin mr-2 text-white inline-block"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="24"
                height="24"
                strokeWidth="2"
              >
                <path d="M12 3a9 9 0 1 0 9 9"></path>
              </svg>
              <span className="text-white font-medium">Processing...</span>
            </div>
          </div>
        </>
      ) : (
        <div ref={containerRef} className="w-full h-full relative">
          <div className="absolute top-2 right-2 z-1 flex gap-2">
            <Button
              onClick={handleExport}
              variant="outline"
              bg="white"
              color="black"
            >
              Download
            </Button>
            <Button
              onClick={() => setImage(null)}
              variant="outline"
              bg="white"
              color="black"
              px={8}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6l-12 12" />
                <path d="M6 6l12 12" />
              </svg>
            </Button>
          </div>
          {memoizedStage}
        </div>
      )}
    </div>
  );
}
